<template>
  <div class="bg-gray-50">
    <div class="qr-editor-container flex bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
    <!-- 左侧我的二维码页面面板 -->
    <div class="w-80 bg-white border-r border-gray-200 flex flex-col">
      
      <!-- 我的二维码页面标题 + 搜索框 -->
      <div class="px-4 h-12 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-2">
        <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider flex-shrink-0">我的二维码页面</h3>
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="搜索..."
            class="w-24 pl-6 pr-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-transparent"
          />
          <div class="absolute inset-y-0 left-0 pl-1.5 flex items-center pointer-events-none">
            <MagnifyingGlassIcon class="h-3 w-3 text-gray-400" />
          </div>
        </div>
      </div>
      
      <!-- 页面列表 -->
      <div class="flex-1 overflow-y-auto">
        <div v-if="loading" class="p-4">
          <div class="animate-pulse space-y-2">
            <div class="h-4 bg-gray-200 rounded"></div>
            <div class="h-4 bg-gray-200 rounded w-4/5"></div>
            <div class="h-4 bg-gray-200 rounded w-3/5"></div>
          </div>
        </div>
        
        <div v-else-if="allPages.length === 0" class="p-8 text-center text-gray-500">
          <QrCodeIcon class="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p class="text-sm">{{ searchQuery ? '没有找到匹配的页面' : '暂无二维码页面' }}</p>
          <p class="text-xs text-gray-400 mt-1">
            {{ searchQuery ? '尝试调整搜索条件' : '创建您的第一个二维码页面' }}
          </p>
        </div>
        
        <div v-else class="divide-y divide-gray-100">
          <div
            v-for="page in allPages"
            :key="page.id"
            class="p-4 hover:bg-gray-50 cursor-pointer transition-colors relative"
            :class="{ 
              'bg-blue-50': currentPage?.id === page.id,
              'opacity-50 pointer-events-none': (loadingPageId && loadingPageId !== page.id) || deletingPageId === page.id
            }"
            @click="selectPage(page)"
          >
            <!-- 加载状态覆盖层 -->
            <div v-if="loadingPageId === page.id" class="absolute inset-0 bg-blue-50 bg-opacity-75 flex items-center justify-center z-10">
              <div class="animate-spin h-5 w-5 text-blue-600">
                <svg class="w-5 h-5" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            </div>

            <!-- 删除状态覆盖层 -->
            <div v-if="deletingPageId === page.id" class="absolute inset-0 bg-red-50 bg-opacity-75 flex items-center justify-center z-10">
              <div class="flex items-center space-x-2">
                <div class="animate-spin h-5 w-5 text-red-600">
                  <svg class="w-5 h-5" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <span class="text-sm text-red-700 font-medium">删除中...</span>
              </div>
            </div>

            <!-- 标题行：标题和操作按钮 -->
            <div class="flex items-start justify-between mb-1">
              <div class="flex-1 mr-2 min-w-0 overflow-hidden">
                <h4 class="text-sm font-medium text-gray-900 truncate" :title="page.name">{{ page.name }}</h4>
                <!-- 创建者标签 -->
                <div v-if="page.created_by_name" class="mt-1">
                  <span :class="[
                    'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium',
                    page.created_by_name === '主账号' ? 'bg-blue-100 text-blue-800' :
                    page.created_by_name === '我的' ? 'bg-green-100 text-green-800' :
                    'bg-purple-100 text-purple-800'
                  ]">
                    {{ page.created_by_name }}
                  </span>
                </div>
              </div>
              <div class="flex-shrink-0">
                <div class="flex space-x-1">
                  <button
                    @click.stop="duplicatePage(page)"
                    :disabled="duplicatingPageId === page.id || deletingPageId === page.id"
                    class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="复制页面"
                  >
                    <div v-if="duplicatingPageId === page.id" class="animate-spin h-4 w-4">
                      <svg class="w-4 h-4" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <DocumentDuplicateIcon v-else class="h-4 w-4" />
                  </button>
                  <button
                    @click.stop="downloadQRCode(page)"
                    :disabled="deletingPageId === page.id"
                    class="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="下载二维码"
                  >
                    <ArrowDownTrayIcon class="h-4 w-4" />
                  </button>
                  <button
                    @click.stop="deletePage(page)"
                    :disabled="deletingPageId === page.id"
                    class="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="删除页面"
                  >
                    <div v-if="deletingPageId === page.id" class="animate-spin w-4 h-4">
                      <svg class="w-4 h-4" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <TrashIcon v-else class="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
            
            <!-- 描述行：铺满整行 -->
            <p class="text-xs text-gray-400 mt-1 line-clamp-2">{{ page.page_description || '暂无描述' }}</p>
            
            <!-- 统计信息行 -->
            <div class="flex items-center mt-2 text-xs text-gray-400">
              <span>访问 {{ page.visit_count || 0 }} 次</span>
              <span class="mx-2">•</span>
              <span>提交 {{ page.submission_count || 0 }} 次</span>
            </div>
            
            <!-- 状态信息行 -->
            <div class="flex items-center mt-1 text-xs text-gray-400">
              <span>{{ formatDate(page.updated_at) }}</span>
              <span v-if="page.is_active" class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                已启用
              </span>
              <span v-else class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                已禁用
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <div v-if="listTotalCount > 0" class="px-4 py-2 border-t border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
        <span class="text-xs text-gray-500">共 {{ listTotalCount }} 个</span>
        <div v-if="listTotalPages > 1" class="flex items-center gap-2">
          <button type="button" @click="goListPage(listPage - 1)" :disabled="listPage <= 1 || loading" class="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-white disabled:opacity-40">上一页</button>
          <span class="text-xs text-gray-600">{{ listPage }} / {{ listTotalPages }}</span>
          <button type="button" @click="goListPage(listPage + 1)" :disabled="listPage >= listTotalPages || loading" class="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-white disabled:opacity-40">下一页</button>
        </div>
      </div>
    </div>

    <!-- 页面基本信息面板 -->
    <div class="w-info bg-white border-l border-r border-gray-200 flex flex-col">
      <!-- 页面基本信息 -->
      <div class="px-4 h-12 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider">页面信息</h3>
        <div class="flex items-center space-x-2">
          <button
            @click="savePage"
            :disabled="isSaving"
            class="flex items-center px-2 py-1 bg-white text-gray-700 text-xs font-medium rounded border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <CheckIcon v-if="!isSaving" class="w-3 h-3 mr-1" />
            <div v-else class="animate-spin w-3 h-3 mr-1">
              <svg class="w-3 h-3" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            {{ isSaving ? '保存中' : '保存' }}
          </button>
          <button
            @click="generateHtml"
            :disabled="isGeneratingHtml || !currentPage || !currentPage.has_custom_html"
            class="flex items-center px-2 py-1 bg-white text-gray-700 text-xs font-medium rounded border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50 transition-colors"
          >
            <div v-if="isGeneratingHtml" class="animate-spin w-3 h-3 mr-1">
            <svg class="w-3 h-3" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
            <CodeBracketIcon v-else class="w-3 h-3 mr-1" />
            {{ isGeneratingHtml ? '生成中' : '生成页面' }}
          </button>
        </div>
      </div>
      <div class="flex-1 overflow-y-auto p-4 space-y-4">
        <div v-if="!currentPage" class="text-center py-8">
          <div class="text-center text-gray-400">
            <InformationCircleIcon class="h-8 w-8 mx-auto mb-2" />
            <p class="text-sm">选择页面后显示详细信息</p>
          </div>
        </div>
        
        <div v-else class="space-y-4">
          <!-- 基本信息 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">页面名称 <span class="text-red-500">*</span></label>
            <input
              v-model="formData.name"
              type="text"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="输入页面名称"
              @input="markDirty"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">页面标题</label>
            <input
              v-model="formData.page_title"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="SEO标题"
              @input="markDirty"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">页面关键词</label>
            <input
              v-model="formData.page_keywords"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="SEO关键词"
              @input="markDirty"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">页面描述</label>
            <textarea
              v-model="formData.page_description"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="SEO描述"
              @input="markDirty"
            ></textarea>
          </div>
          
          <!-- Amazon Asin -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Amazon Asin</label>
            <input
              v-model="formData.amazon_asin"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="输入 Amazon ASIN 编号，如：B08N5WRWNW"
              @input="markDirty"
            />
          </div>

          <!-- 客服邮箱 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">客服邮箱</label>
            <input
              v-model="formData.customer_service_email"
              type="email"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="输入客服邮箱（mailto 收件人），如：support@yourbrand.com"
              @input="markDirty"
            />
            <p class="text-xs text-gray-400 mt-1">客户上传截图后，页面会用此邮箱作为邮件收件人（留空则用默认/创建者邮箱）</p>
          </div>
          
          <!-- 客户分组 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">客户分组</label>
            <select
              v-model="formData.customer_group"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              @change="markDirty"
            >
              <option :value="null">默认分组（二维码客户）</option>
              <option v-for="group in customerGroups" :key="group.id" :value="group.id">
                {{ group.name }}
              </option>
            </select>
            <p class="text-xs text-gray-400 mt-1">通过此页面提交的客户将归入所选分组</p>
          </div>
          
          <!-- 客户来源 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">客户来源</label>
            <input
              v-model="formData.customer_source"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="留空则默认为「售后二维码」"
              @input="markDirty"
            />
            <p class="text-xs text-gray-400 mt-1">客户同步时的来源标识，用于区分不同渠道</p>
          </div>
          
          <!-- 预设主题 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">预设主题</label>
            <input
              v-model="formData.preset_subject"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="输入预设的问题主题"
              @input="markDirty"
            />
          </div>
          
          <!-- 预设内容 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">预设内容</label>
            <textarea
              v-model="formData.preset_content"
              rows="4"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder="输入预设的问题内容描述"
              @input="markDirty"
            ></textarea>
          </div>
          
          <!-- 产品搜索 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">搜索产品</label>
            <div class="relative">
              <input
                v-model="productSearchQuery"
                @input="searchProducts($event.target.value)"
                type="text"
                class="w-full px-3 py-2 pl-8 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="输入产品名称或编号..."
              />
              <div class="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                <MagnifyingGlassIcon class="h-3 w-3 text-gray-400" />
              </div>
              <div v-if="isSearchingProducts" class="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                <svg class="animate-spin h-3 w-3 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              
              <!-- 搜索结果 -->
              <div v-if="productSearchResults.length > 0" class="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                <div 
                  v-for="product in productSearchResults" 
                  :key="product.id" 
                  @click="addProduct(product)"
                  class="flex items-center justify-between p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                >
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-gray-900 text-xs truncate">{{ product.name }}</div>
                    <div class="text-xs text-gray-500">
                      <span class="font-mono">{{ product.code }}</span>
                      <span v-if="product.brand" class="ml-2">· {{ product.brand }}</span>
                    </div>
                  </div>
                  <button class="ml-2 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                    添加
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 已绑定产品列表 -->
          <div v-if="boundProducts.length > 0">
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium text-gray-700">
                已绑定产品
              </label>
              <span class="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                {{ boundProducts.length }}
              </span>
            </div>
            
            <div class="space-y-1 max-h-24 overflow-y-auto custom-scrollbar">
              <div 
                v-for="product in boundProducts" 
                :key="product.id"
                class="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded text-xs"
              >
                <div class="flex-1 min-w-0">
                  <div class="font-medium text-gray-900 truncate">{{ product.name }}</div>
                  <div class="text-xs text-gray-500">
                    <span class="font-mono">{{ product.code }}</span>
                    <span v-if="product.brand" class="ml-2">· {{ product.brand }}</span>
                  </div>
                </div>
                <button 
                  @click="removeProduct(product)"
                  class="ml-2 p-0.5 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
                  title="移除产品"
                >
                  <svg class="h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          
          
          <div style="display:none">
            <label class="block text-sm font-medium text-gray-700 mb-2">品牌Logo</label>
            
            <!-- Logo上传区域 -->
            <div class="relative">
              <!-- 已有Logo预览 -->
              <div v-if="formData.brand_logo && !uploadingLogo" class="relative group">
                <div class="w-full h-32 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden">
                  <img 
                    :src="formData.brand_logo" 
                    alt="Brand Logo" 
                    class="max-w-full max-h-full object-contain"
                    @error="handleImageError"
                  />
                </div>
                <div class="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg flex items-center justify-center">
                  <div class="flex space-x-2">
                    <button
                      @click="changeLogo"
                      type="button"
                      class="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                    >
                      更换
                    </button>
                    <button
                      @click="removeLogo"
                      type="button"
                      class="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                    >
                      删除
                    </button>
                  </div>
                </div>
              </div>
              
              <!-- 上传区域 -->
              <div 
                v-else
                @click="triggerFileInput"
                @dragover.prevent="handleDragOver"
                @dragleave.prevent="handleDragLeave"
                @drop.prevent="handleDrop"
                :class="[
                  'w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200',
                  isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400',
                  uploadingLogo ? 'pointer-events-none' : ''
                ]"
              >
                <div class="h-full flex flex-col items-center justify-center">
                  <div v-if="uploadingLogo" class="text-center">
                    <div class="animate-spin h-8 w-8 mx-auto mb-2">
                      <svg class="w-8 h-8 text-blue-600" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <p class="text-sm text-gray-600">上传中...</p>
                    <p class="text-xs text-gray-400 mt-1">{{ uploadProgress }}%</p>
                  </div>
                  <div v-else class="text-center">
                    <div class="h-8 w-8 mx-auto mb-2 text-gray-400">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                      </svg>
                    </div>
                    <p class="text-sm font-medium text-gray-700">上传品牌Logo</p>
                    <p class="text-xs text-gray-500 mt-1">点击或拖拽文件到此处</p>
                    <p class="text-xs text-gray-400 mt-1">支持 PNG, JPG, GIF (最大 5MB)</p>
                  </div>
                </div>
              </div>
              
              <!-- 隐藏的文件输入 -->
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                class="hidden"
                @change="handleFileSelect"
              />
            </div>
          </div>
          
          <!-- 自定义域名配置 -->
          <div class="border-t pt-4 mt-4">
            <div class="flex items-center justify-between mb-3">
              <h4 class="text-sm font-medium text-gray-900">自定义域名配置</h4>
              <div class="flex gap-2">
                <button
                  v-if="currentPage && !formData.custom_domain"
                  @click="openDomainModal"
                  class="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded-md hover:bg-blue-100 transition-colors"
                >
                  + 绑定域名
                </button>
                <button
                  v-if="currentPage && formData.custom_domain"
                  @click="clearDomain"
                  class="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-300 rounded-md hover:bg-red-100 transition-colors"
                >
                  清空域名
                </button>
              </div>
            </div>
            
            <!-- 域名状态卡片 -->
            <div v-if="formData.custom_domain" class="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <div class="text-sm font-medium text-gray-900 mb-2">{{ formData.custom_domain }}</div>
                  <div class="flex items-center gap-3 text-xs">
                    <span :class="domainStatus.verified ? 'text-green-600 font-medium' : 'text-orange-600'">
                      {{ domainStatus.verified ? '✓ 已验证' : '○ 未验证' }}
                    </span>
                    <span :class="domainStatus.ssl_enabled ? 'text-green-600 font-medium' : 'text-gray-500'">
                      {{ domainStatus.ssl_enabled ? '🔒 SSL已启用' : '🔓 待配置SSL' }}
                    </span>
                  </div>
                </div>
                
                <!-- 根据状态显示不同的操作按钮 -->
                <button
                  v-if="!domainStatus.verified"
                  @click="openDomainModal"
                  class="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  验证域名
                </button>
                <button
                  v-else-if="domainStatus.verified && !domainStatus.ssl_enabled"
                  @click="openDomainModal"
                  class="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors whitespace-nowrap"
                >
                  配置SSL
                </button>
              </div>
            </div>
            
            <p v-else class="text-xs text-gray-500">
              绑定自定义域名，让用户通过您的品牌域名访问页面
            </p>
          </div>
          
          <!-- 访问链接 -->
          <div v-if="currentPage" class="border-t pt-4">
            <div class="flex items-center justify-between mb-3">
              <h4 class="text-sm font-medium text-gray-900">访问链接</h4>
              <button
                v-if="currentPage.full_url"
                @click="copyUrl(currentPage.full_url)"
                class="text-xs text-blue-600 hover:text-blue-700"
                title="复制链接"
              >
                复制
              </button>
            </div>

            <div v-if="currentPage.full_url" class="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <a 
                :href="currentPage.full_url" 
                target="_blank"
                class="text-sm text-blue-600 hover:text-blue-700 hover:underline break-all block"
              >
                {{ currentPage.full_url }}
              </a>
              <p class="text-xs text-gray-500 mt-2">
                <svg class="inline w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
                点击访问二维码页面
              </p>
            </div>
          </div>
          
          <!-- 页面统计 -->
          <div v-if="currentPage" class="border-t pt-4 mt-4">
            <h4 class="text-sm font-medium text-gray-900 mb-3">页面统计</h4>
            <div class="space-y-2">
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">访问次数:</span>
                <button 
                  @click="openVisitStatsModal"
                  class="font-medium text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
                  :title="'点击查看访问详情'"
                >
                  {{ currentPage.visit_count || 0 }}
                </button>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">提交次数:</span>
                <button 
                  @click="openSubmissionStatsModal"
                  class="font-medium text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
                  :title="'点击查看提交详情'"
                >
                  {{ currentPage.submission_count || 0 }}
                </button>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">创建时间:</span>
                <span class="font-medium">{{ formatDateTime(currentPage.created_at) }}</span>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">更新时间:</span>
                <span class="font-medium">{{ formatDateTime(currentPage.updated_at) }}</span>
              </div>
            </div>
          </div>
          
          <!-- 二维码显示 -->
          <div v-if="currentPage && currentPage.qr_code_url" class="border-t pt-4 mt-4">
            <h4 class="text-sm font-medium text-gray-900 mb-3">二维码</h4>
            <div class="text-center">
              <img :src="currentPage.qr_code_url" alt="二维码" class="w-32 h-32 mx-auto border rounded" />
              <p class="text-xs text-gray-500 mt-2">扫描二维码访问页面</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 编辑器面板 -->
    <div class="flex-1 flex flex-col bg-white border-l border-r border-gray-200">
      <!-- 顶部工具栏 -->
      <div class="px-4 h-12 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider">HTML内容编辑</h3>
        <div class="flex items-center space-x-2">
          <button
            v-if="currentPage"
            @click="openFullscreenEditor"
            class="flex items-center px-2 py-1 bg-white text-gray-700 text-xs font-medium rounded border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 transition-colors"
          >
            <CodeBracketIcon class="w-3 h-3 mr-1" />
            全屏
          </button>
          <button
            @click="savePage"
            :disabled="isSaving"
            class="flex items-center px-2 py-1 bg-white text-gray-700 text-xs font-medium rounded border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
          <CheckIcon v-if="!isSaving" class="w-3 h-3 mr-1" />
          <div v-else class="animate-spin w-3 h-3 mr-1">
            <svg class="w-3 h-3" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            {{ isSaving ? '保存中' : '保存' }}
          </button>
        </div>
      </div>
      
      <!-- 编辑表单区域 -->
      <div class="flex-1 flex flex-col overflow-hidden">
        <div v-if="!currentPage" class="text-center py-8">
          <div class="text-center text-gray-400">
            <InformationCircleIcon class="h-8 w-8 mx-auto mb-2" />
            <p class="text-sm">选择页面后显示HTML编辑器</p>
            <p class="text-xs text-gray-400 mt-1">从左侧选择现有页面，或从右侧模板库添加新页面</p>
          </div>
        </div>
        <HTMLEditor
          v-else
          ref="editorInstance"
          v-model="formData.html_content"
          :height="calculateEditorHeight()"
          @change="markDirty"
        />
      </div>
    </div>

    <!-- 二维码模板列表面板 -->
    <div class="w-80 bg-white border-l border-gray-200 flex flex-col">
      <!-- 模板列表标题 + 搜索框 -->
      <div class="px-4 h-12 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-2">
        <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider flex-shrink-0">二维码模板库</h3>
        <div class="relative">
          <input
            v-model="templateSearchQuery"
            type="text"
            placeholder="搜索..."
            class="w-24 pl-6 pr-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-transparent"
          />
          <div class="absolute inset-y-0 left-0 pl-1.5 flex items-center pointer-events-none">
            <MagnifyingGlassIcon class="h-3 w-3 text-gray-400" />
          </div>
        </div>
      </div>
      
      <!-- 模板列表 -->
      <div class="flex-1 overflow-y-auto">
        <div v-if="loadingTemplates" class="p-4 space-y-4">
          <div v-for="i in 3" :key="`loading-${i}`" class="animate-pulse">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div class="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div class="flex gap-1 mb-2">
                  <div class="h-5 bg-gray-200 rounded w-16"></div>
                  <div class="h-5 bg-gray-200 rounded w-12"></div>
                </div>
                <div class="flex items-center justify-between">
                  <div class="h-3 bg-gray-200 rounded w-1/3"></div>
                  <div class="flex space-x-2">
                    <div class="w-12 h-6 bg-gray-200 rounded"></div>
                    <div class="w-12 h-6 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else-if="templates.length === 0" class="p-8 text-center text-gray-500">
          <DocumentTextIcon class="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p class="text-sm">{{ templateSearchQuery ? '没有找到匹配的模板' : '暂无可用模板' }}</p>
          <p v-if="templateSearchQuery" class="text-xs text-gray-400 mt-1">尝试调整搜索关键词</p>
        </div>
        
        <div v-else class="divide-y divide-gray-100">
          <div
            v-for="template in templates"
            :key="template.id"
            class="p-4 hover:bg-gray-50 transition-colors"
            :class="{ 'bg-blue-50': formData.template === template.id }"
          >
            <div class="flex items-center justify-between">
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between mb-1">
                  <h4 class="text-sm font-medium text-gray-900 truncate flex-1 mr-2">{{ template.name }}</h4>
                  <span class="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                    {{ template.category_name || '未分类' }}
                  </span>
                </div>
                
                <!-- 模板特性标签 -->
                <div class="flex flex-wrap gap-1 mt-2">
                  <span v-if="template.supports_products" class="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-50 text-green-600">
                    支持产品
                  </span>
                  <span v-if="template.supports_logo" class="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-600">
                    支持Logo
                  </span>
                  <span v-if="template.language" class="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-50 text-gray-600">
                    {{ template.language }}
                  </span>
                </div>
                
                <div class="flex items-center justify-between mt-2">
                  <span class="text-xs text-gray-400">使用 {{ template.usage_count || 0 }} 次</span>
                  
                  <!-- 操作按钮 -->
                  <div class="flex space-x-2">
                    <button
                      @click="previewTemplate(template)"
                      class="px-2 py-1 text-xs text-gray-600 hover:text-gray-900 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                    >
                      预览
                    </button>
                    <button
                      @click="addTemplateToPage(template)"
                      :disabled="addingTemplateId === template.id"
                      class="px-2 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <span v-if="addingTemplateId === template.id">添加中...</span>
                      <span v-else>添加</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
    
    <!-- 域名配置模态框 -->
    <Teleport to="body">
      <div v-if="showDomainModal" class="fixed inset-0 z-50 overflow-y-auto" @click.self="closeDomainModal">
        <div class="flex min-h-screen items-center justify-center p-4">
          <!-- 背景遮罩 -->
          <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="closeDomainModal"></div>
          
          <!-- 模态框内容 -->
          <div class="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full transform transition-all" @click.stop>
            <!-- 头部 -->
            <div class="px-6 py-4 border-b border-gray-200">
              <div class="flex items-center justify-between">
                <h3 class="text-lg font-semibold text-gray-900">自定义域名配置</h3>
                <button @click="closeDomainModal" class="text-gray-400 hover:text-gray-600 transition-colors">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <!-- 步骤指示器 -->
            <div class="px-6 py-4 bg-gray-50">
              <div class="flex items-center justify-between">
                <!-- 步骤1 -->
                <div class="flex items-center flex-1 min-w-0">
                  <div :class="[
                    'w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-semibold transition-all',
                    modalStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                  ]">
                    {{ domainStatus.verified && modalStep > 1 ? '✓' : '1' }}
                  </div>
                  <div class="ml-3 text-sm min-w-0">
                    <div :class="['font-medium truncate', modalStep >= 1 ? 'text-gray-900' : 'text-gray-500']">配置DNS</div>
                    <div class="text-xs text-gray-500 truncate">设置CNAME记录</div>
                  </div>
                </div>
                
                <div :class="['flex-shrink-0 w-12 h-0.5 mx-2', modalStep > 1 ? 'bg-blue-600' : 'bg-gray-200']"></div>
                
                <!-- 步骤2 -->
                <div class="flex items-center flex-1 min-w-0">
                  <div :class="[
                    'w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-semibold transition-all',
                    modalStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                  ]">
                    {{ domainStatus.verified && modalStep > 2 ? '✓' : '2' }}
                  </div>
                  <div class="ml-3 text-sm min-w-0">
                    <div :class="['font-medium truncate', modalStep >= 2 ? 'text-gray-900' : 'text-gray-500']">验证域名</div>
                    <div class="text-xs text-gray-500 truncate">检查DNS解析</div>
                  </div>
                </div>
                
                <div :class="['flex-shrink-0 w-12 h-0.5 mx-2', modalStep > 2 ? 'bg-blue-600' : 'bg-gray-200']"></div>
                
                <!-- 步骤3 -->
                <div class="flex items-center flex-1 min-w-0">
                  <div :class="[
                    'w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-semibold transition-all',
                    modalStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                  ]">
                    {{ domainStatus.ssl_enabled ? '✓' : '3' }}
                  </div>
                  <div class="ml-3 text-sm min-w-0">
                    <div :class="['font-medium truncate', modalStep >= 3 ? 'text-gray-900' : 'text-gray-500']">启用SSL</div>
                    <div class="text-xs text-gray-500 truncate">申请HTTPS证书</div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 主体内容 -->
            <div class="px-6 py-6">
              <!-- 步骤1：配置DNS -->
              <div v-if="modalStep === 1" class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    域名地址 <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="modalDomain"
                    type="text"
                    class="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="例如：review.yourbrand.com"
                  />
                  <p class="text-xs text-gray-500 mt-1">
                    请输入您要绑定的完整域名
                  </p>
                </div>
                
                <div class="border border-gray-200 rounded-lg p-4">
                  <h5 class="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <svg class="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    DNS配置说明
                  </h5>
                  <div class="space-y-2.5 text-sm text-gray-700">
                    <p><span class="font-medium text-gray-900">1.</span> 登录您的域名DNS服务商管理后台</p>
                    <p><span class="font-medium text-gray-900">2.</span> 添加一条CNAME记录：</p>
                    <div class="bg-gray-50 rounded-lg p-3 mt-2 font-mono text-xs space-y-1.5 border border-gray-200">
                      <div class="flex justify-between">
                        <span class="text-gray-500">类型：</span>
                        <span class="font-semibold text-gray-900">CNAME</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-gray-500">主机记录：</span>
                        <span class="font-semibold text-gray-900">{{ getHostRecord(modalDomain) }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-gray-500">记录值：</span>
                        <span class="font-semibold text-blue-600">apis.bubu.im</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-gray-500">TTL：</span>
                        <span class="font-semibold text-gray-900">600（或默认值）</span>
                      </div>
                    </div>
                    <p class="pt-1"><span class="font-medium text-gray-900">3.</span> 等待DNS解析生效（通常5-30分钟）</p>
                    <p><span class="font-medium text-gray-900">4.</span> 点击"下一步"进行验证</p>
                  </div>
                </div>
              </div>
              
              <!-- 步骤2：验证域名 -->
              <div v-if="modalStep === 2" class="space-y-4">
                <div class="py-6">
                  <h4 class="text-base font-semibold text-gray-900 mb-2">验证域名解析</h4>
                  <p class="text-sm text-gray-600 mb-6">
                    我们将检查您的域名CNAME记录是否已正确配置
                  </p>
                  
                  <div class="border border-gray-200 rounded-lg p-4 mb-6">
                    <div class="text-sm space-y-3">
                      <div class="flex justify-between items-center">
                        <span class="text-gray-600">域名：</span>
                        <span class="font-semibold text-gray-900">{{ modalDomain || formData.custom_domain }}</span>
                      </div>
                      <div class="flex justify-between items-center pt-2 border-t border-gray-100">
                        <span class="text-gray-600">状态：</span>
                        <span :class="domainStatus.verified ? 'text-green-600 font-semibold' : 'text-orange-600 font-semibold'">
                          {{ domainStatus.verified ? '已验证' : '待验证' }}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    @click="verifyDomainInModal"
                    :disabled="isVerifyingDomain"
                    class="w-full px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  >
                    <template v-if="isVerifyingDomain">
                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      验证中
                    </template>
                    <template v-else>验证域名</template>
                  </button>
                  
                  <p class="mt-4 text-xs text-gray-500">
                    如果验证失败，请检查DNS配置是否正确，并等待解析生效
                  </p>
                </div>
              </div>
              
              <!-- 步骤3：启用SSL -->
              <div v-if="modalStep === 3" class="space-y-4">
                <div class="py-6">
                  <h4 class="text-base font-semibold text-gray-900 mb-2">配置SSL证书</h4>
                  <p class="text-sm text-gray-600 mb-6">
                    为您的域名申请免费的HTTPS证书，保护用户数据安全
                  </p>
                  
                  <div class="border border-gray-200 rounded-lg p-4 mb-6">
                    <div class="text-sm space-y-3">
                      <div class="flex justify-between items-center">
                        <span class="text-gray-600">域名：</span>
                        <span class="font-semibold text-gray-900">{{ formData.custom_domain }}</span>
                      </div>
                      <div class="flex justify-between items-center pt-2 border-t border-gray-100">
                        <span class="text-gray-600">SSL状态：</span>
                        <span :class="domainStatus.ssl_enabled ? 'text-green-600 font-semibold' : 'text-gray-500 font-semibold'">
                          {{ domainStatus.ssl_enabled ? '已启用' : '待配置' }}
                        </span>
                      </div>
                      <div v-if="domainStatus.ssl_enabled && domainStatus.full_url" class="pt-2 border-t border-gray-100">
                        <span class="text-gray-600 block mb-1.5">访问地址：</span>
                        <a :href="domainStatus.full_url" target="_blank" class="text-blue-600 hover:underline text-xs break-all">
                          {{ domainStatus.full_url }}
                        </a>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    v-if="!domainStatus.ssl_enabled"
                    @click="applySSLInModal"
                    :disabled="isSettingUpSSL"
                    class="w-full px-6 py-2.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  >
                    <template v-if="isSettingUpSSL">
                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      申请中
                    </template>
                    <template v-else>启用SSL</template>
                  </button>
                  
                  <div v-else class="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div class="flex items-start">
                      <svg class="w-5 h-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                      </svg>
                      <div>
                        <h5 class="text-sm font-semibold text-green-900">SSL证书已成功配置</h5>
                        <p class="text-xs text-green-700 mt-1">您的自定义域名现已支持HTTPS安全访问</p>
                      </div>
                    </div>
                  </div>
                  
                  <p v-if="!domainStatus.ssl_enabled" class="mt-4 text-xs text-gray-500">
                    SSL证书申请需要1-2分钟，请耐心等待
                  </p>
                </div>
              </div>
            </div>
            
            <!-- 底部按钮 -->
            <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl flex justify-between">
              <button
                v-if="modalStep > 1 && !domainStatus.ssl_enabled"
                @click="modalStep--"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                上一步
              </button>
              <div v-else></div>
              
              <div class="flex gap-2">
                <button
                  @click="closeDomainModal"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  {{ domainStatus.ssl_enabled && modalStep === 3 ? '完成' : '取消' }}
                </button>
                <button
                  v-if="modalStep === 1"
                  @click="saveAndNextStep"
                  :disabled="!modalDomain || isSaving"
                  class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  <template v-if="isSaving">
                    <svg class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    保存中
                  </template>
                  <template v-else>下一步</template>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
    
    <!-- 全屏HTML编辑器模态框 -->
    <Teleport to="body">
      <div v-if="showFullscreenEditor" class="fixed inset-0 z-50 bg-white">
        <!-- 编辑器头部 -->
        <div class="bg-gray-800 border-b border-gray-700 px-6 py-3 flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <h3 class="text-lg font-medium text-white">HTML 全屏编辑器</h3>
            <span class="text-sm text-gray-400">{{ currentPage?.name }}</span>
          </div>
          <div class="flex items-center space-x-3">
            <button
              @click="saveFullscreenAndClose"
              class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium transition-colors"
            >
              保存并关闭
            </button>
            <button
              @click="closeFullscreenEditor"
              class="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 text-sm transition-colors"
            >
              取消
            </button>
          </div>
        </div>

        <!-- 编辑器内容 -->
        <div class="h-[calc(100vh-57px)]">
          <HTMLEditor
            ref="fullscreenHtmlEditorInstance"
            v-model="fullscreenHtmlContent"
            :height="'calc(100vh - 57px)'"
          />
        </div>
      </div>
    </Teleport>
    
    <!-- 访问统计模态框 -->
    <VisitStatsModal
      v-model:visible="showVisitStatsModal"
      :item-id="currentPage?.id"
      :item-info="currentPage"
      :api-function="visitStatsApiFunction"
    />
    
    <!-- 表单提交统计模态框 -->
    <ContactStatsModal
      v-model:visible="showSubmissionStatsModal"
      :item-id="currentPage?.id"
      :item-info="currentPage"
      :api-function="submissionStatsApiFunction"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useToast } from 'vue-toastification'
import {
  QrCodeIcon, MagnifyingGlassIcon, DocumentDuplicateIcon,
  TrashIcon, CheckIcon, EyeIcon, CodeBracketIcon, DocumentTextIcon,
  InformationCircleIcon, ArrowDownTrayIcon
} from '@heroicons/vue/24/outline'
import HTMLEditor from '@/components/common/HTMLEditor.vue'
import VisitStatsModal from '@/components/common/VisitStatsModal.vue'
import ContactStatsModal from '@/components/common/ContactStatsModal.vue'
import { qrCodeService, qrTemplateService, qrProductService } from '@/services/qrCodeService'
import { productService } from '@/services/productService'
import { customerGroupService } from '@/services/customerService'
import api from '@/utils/api'

const toast = useToast()

// 编辑器实例引用
const editorInstance = ref(null)
const fullscreenHtmlEditorInstance = ref(null)

// 全屏编辑器状态
const showFullscreenEditor = ref(false)
const fullscreenHtmlContent = ref('')

// 计算编辑器高度
const calculateEditorHeight = () => {
  if (typeof window !== 'undefined') {
    const windowHeight = window.innerHeight
    return Math.max(450, windowHeight - 214)
  }
  return 550
}

// 状态管理
const loading = ref(false)
const isSaving = ref(false)
const isDirty = ref(false)
const isGeneratingHtml = ref(false)

const searchQuery = ref('')
const listPage = ref(1)
const listTotalCount = ref(0)
const listTotalPages = ref(1)
const listPageSize = 20
const templateSearchQuery = ref('')
const templateLanguageFilter = ref('')
const templateCategoryFilter = ref('')
const allPages = ref([])  // 存储所有页面数据
const allTemplates = ref([])  // 存储所有模板数据
const products = ref([])
const currentPage = ref(null)
const loadingTemplates = ref(false)
const loadingPageId = ref(null)
const addingTemplateId = ref(null)
const duplicatingPageId = ref(null)
const deletingPageId = ref(null)
const selectedProducts = ref([])
const templateLanguages = ref([])
const templateCategories = ref([])

// Logo上传相关状态
const fileInput = ref(null)
const uploadingLogo = ref(false)
const uploadProgress = ref(0)
const isDragOver = ref(false)

// 产品搜索相关状态
const boundProducts = ref([])
const productSearchQuery = ref('')
const productSearchResults = ref([])
const isSearchingProducts = ref(false)
let searchTimeout = null // 防抖定时器

// 自定义域名相关状态
const domainStatus = ref({
  verified: false,
  ssl_enabled: false,
  full_url: '',
  instructions: {}
})
const isVerifyingDomain = ref(false)
const isSettingUpSSL = ref(false)
const isRefreshingDomain = ref(false)

// 域名配置模态框状态
const showDomainModal = ref(false)
const modalStep = ref(1) // 1: 配置DNS, 2: 验证域名, 3: 启用SSL
const modalDomain = ref('')

// 访问统计模态框
const showVisitStatsModal = ref(false)
const visitStatsApiFunction = (id, page, pageSize) => qrCodeService.getVisits(id, page, pageSize)

// 表单提交统计模态框
const showSubmissionStatsModal = ref(false)
const submissionStatsApiFunction = (id, page, pageSize) => qrCodeService.getSubmissions(id, page, pageSize)

// 客户分组列表
const customerGroups = ref([])

// 表单数据
const formData = reactive({
  name: '',
  page_title: '',
  page_keywords: '',
  page_description: '',
  amazon_asin: '',
  preset_subject: '',
  preset_content: '',
  customer_service_email: '',
  customer_group: null,
  customer_source: '',
  template: '',
  brand_logo: '',
  html_content: '',
  use_custom_html: true,
  is_active: true,
  product_ids: [],
  custom_domain: ''
})

// 计算属性
const isEditing = computed(() => !!(currentPage.value?.id))

// 前端搜索过滤 - 模板列表
const templates = computed(() => {
  if (!templateSearchQuery.value) {
    return allTemplates.value
  }
  return allTemplates.value.filter(template => 
    template.name?.toLowerCase().includes(templateSearchQuery.value.toLowerCase())
  )
})

// 辅助函数：从域名中提取主机记录
const getHostRecord = (domain) => {
  if (!domain) return 'www'
  
  const parts = domain.split('.')
  
  // 如果只有2段（如 example.com），通常不会这样配置CNAME，返回提示
  if (parts.length <= 2) {
    return domain // 或者返回 '@'
  }
  
  // 如果有3段或以上（如 www.example.com, review.yourbrand.com）
  // 主机记录是除了最后两段（主域名）之外的所有部分
  // 例如: www.example.com -> www
  //      api.sub.example.com -> api.sub
  const hostRecord = parts.slice(0, -2).join('.')
  return hostRecord || 'www'
}

// 方法
const loadPages = async (page = listPage.value) => {
  loading.value = true
  try {
    const params = { page, page_size: listPageSize }
    if (searchQuery.value.trim()) params.search = searchQuery.value.trim()
    const response = await qrCodeService.getPages(params)
    if (response.success) {
      allPages.value = response.results || []
      listTotalCount.value = response.count || 0
      listTotalPages.value = response.totalPages || 1
      listPage.value = page
    }
  } catch (error) {
    toast.error('加载二维码页面失败')
    console.error('加载二维码页面失败:', error)
  } finally {
    loading.value = false
  }
}

const goListPage = (page) => {
  if (page < 1 || page > listTotalPages.value || page === listPage.value) return
  loadPages(page)
}

let listSearchTimer = null
watch(searchQuery, () => {
  clearTimeout(listSearchTimer)
  listSearchTimer = setTimeout(() => {
    listPage.value = 1
    loadPages(1)
  }, 300)
})

const loadCustomerGroups = async () => {
  try {
    const response = await customerGroupService.getGroups()
    if (response.success) {
      customerGroups.value = response.data?.results || response.data || []
    }
  } catch (error) {
    console.error('加载客户分组失败:', error)
  }
}

const loadTemplates = async () => {
  loadingTemplates.value = true
  try {
    const response = await qrTemplateService.getTemplates()
    if (response.success) {
      const templateList = response.results || response.data
      allTemplates.value = templateList
      
      // 提取语言和类别选项
      updateFilterOptions(templateList)
    }
  } catch (error) {
    console.error('加载模板失败:', error)
  } finally {
    loadingTemplates.value = false
  }
}

const updateFilterOptions = (templateList) => {
  // 提取所有语言选项
  const languageSet = new Set()
  const categorySet = new Set()
  
  templateList.forEach(template => {
    if (template.language) {
      languageSet.add(template.language)
    }
    if (template.category_name) {
      categorySet.add({
        id: template.category_id || template.category,
        name: template.category_name
      })
    }
  })
  
  templateLanguages.value = Array.from(languageSet).sort()
  templateCategories.value = Array.from(categorySet, item => 
    typeof item === 'string' ? { id: item, name: item } : item
  ).filter((item, index, arr) => 
    arr.findIndex(x => x.id === item.id) === index
  ).sort((a, b) => a.name.localeCompare(b.name))
}

const loadProducts = async () => {
  try {
    const response = await qrProductService.getProducts()
    if (response.success) {
      products.value = response.results || response.data
    }
  } catch (error) {
    console.error('加载产品失败:', error)
  }
}

// 产品搜索方法（带防抖）
const searchProducts = (query) => {
  // 清除之前的定时器
  if (searchTimeout) {
    clearTimeout(searchTimeout)
    searchTimeout = null
  }
  
  if (!query || query.trim() === '') {
    productSearchResults.value = []
    isSearchingProducts.value = false
    return
  }
  
  // 防抖：延迟执行搜索
  searchTimeout = setTimeout(async () => {
    isSearchingProducts.value = true
    
    try {
      // 使用真实API搜索产品
      const result = await productService.getProductSuggestions(query.trim(), 10)
      
      if (result.success) {
        // 过滤已经绑定的产品和非活跃产品
        const boundProductIds = boundProducts.value.map(p => p.id)
        const filteredProducts = result.data.filter(product => 
          !boundProductIds.includes(product.id) && product.is_active !== false
        )
        
        productSearchResults.value = filteredProducts
      } else {
        productSearchResults.value = []
      }
    } catch (error) {
      console.error('产品搜索失败:', error)
      productSearchResults.value = []
      
      // 只有在非取消错误时才显示错误提示
      if (error.name !== 'CanceledError') {
        toast.error('搜索产品失败，请重试')
      }
    } finally {
      isSearchingProducts.value = false
    }
  }, 300) // 300ms 防抖延迟
}

const addProduct = (product) => {
  if (boundProducts.value.find(p => p.id === product.id)) {
    return
  }
  
  boundProducts.value.push(product)
  productSearchResults.value = productSearchResults.value.filter(p => p.id !== product.id)
  
  // 更新formData中的产品IDs（如果formData有product_ids字段）
  if (!formData.product_ids) {
    formData.product_ids = []
  }
  formData.product_ids.push(product.id)
  
  markDirty()
  toast.success(`已添加产品 ${product.name}`)
}

const removeProduct = (product) => {
  boundProducts.value = boundProducts.value.filter(p => p.id !== product.id)
  
  // 从formData中移除产品ID
  if (formData.product_ids) {
    formData.product_ids = formData.product_ids.filter(id => id !== product.id)
  }
  
  markDirty()
  toast.success(`已移除产品 ${product.name}`)
}

const selectPage = async (page) => {
  loadingPageId.value = page.id
  
  try {
    const response = await qrCodeService.getPage(page.id)
    
    if (response.success) {
      currentPage.value = response.data
      fillForm(response.data)
      isDirty.value = false
    } else {
      toast.error(response.message || '获取页面详情失败')
    }
  } catch (error) {
    toast.error('获取页面详情失败')
    console.error('获取页面详情失败:', error)
  } finally {
    loadingPageId.value = null
  }
}

const selectTemplate = (template) => {
  formData.template = template.id
  markDirty()
}

const previewTemplate = (template) => {
  try {
    // 构建模板预览URL - 直接使用后端API地址
    const backendUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
    const previewUrl = `${backendUrl}/qr-codes/template-preview/${template.id}/`
    
    // 手机模式预览窗口尺寸 - 增大以便更好预览
    const mobileWidth = 450   // 增大宽度
    const mobileHeight = 850  // 增大高度
    
    // 计算居中位置
    const screenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX
    const screenTop = window.screenTop !== undefined ? window.screenTop : window.screenY
    const width = window.innerWidth || document.documentElement.clientWidth || screen.width
    const height = window.innerHeight || document.documentElement.clientHeight || screen.height
    
    const left = (width / 2) - (mobileWidth / 2) + screenLeft
    const top = (height / 2) - (mobileHeight / 2) + screenTop
    
    // 在新窗口打开预览 - 手机模式
    const previewWindow = window.open(
      previewUrl, 
      'qr_template_preview',
      `width=${mobileWidth},height=${mobileHeight},left=${left},top=${top},scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no`
    )
    
    // 检查是否成功打开窗口
    if (!previewWindow) {
      toast.error('无法打开预览窗口，请检查浏览器弹窗设置')
      return
    }
    
    // 设置窗口标题和移动端样式
    previewWindow.addEventListener('load', () => {
      try {
        // 尝试设置移动端视口
        const viewportMeta = previewWindow.document.createElement('meta')
        viewportMeta.name = 'viewport'
        viewportMeta.content = 'width=device-width, initial-scale=1.0, user-scalable=no'
        
        const existingViewport = previewWindow.document.querySelector('meta[name="viewport"]')
        if (existingViewport) {
          existingViewport.remove()
        }
        previewWindow.document.head.appendChild(viewportMeta)
        
        // 设置窗口标题
        previewWindow.document.title = `📱 ${template.name} - 移动端预览`
      } catch (e) {
        // 跨域限制时静默失败
        console.log('预览窗口样式设置受跨域限制')
      }
    })
    
    // 监听窗口加载失败
    previewWindow.addEventListener('error', () => {
      toast.error('模板预览加载失败')
    })
    
  } catch (error) {
    console.error('预览模板失败:', error)
    toast.error('预览功能暂时不可用')
  }
}

const addTemplateToPage = async (template) => {
  // 防止重复点击
  if (addingTemplateId.value) return
  
  addingTemplateId.value = template.id
  
  try {
    // 构建新页面数据
    const now = new Date()
    const timeStr = now.toLocaleString('zh-CN', { 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit', 
      minute: '2-digit'
    }).replace(/[\/\s:]/g, '')
    
    const newPageData = {
      name: `${template.name}_${timeStr}`, // 使用模板名称加时间
      page_title: template.name,
      page_description: `基于 ${template.name} 模板创建的二维码页面`,
      template: template.id,
      use_custom_html: true,
      is_active: true,
      product_ids: [] // 空的产品列表
    }
    
    // 创建新的二维码页面
    const response = await qrCodeService.createPage(newPageData)
    
    if (response.success) {
      const createdPage = response.data
      
      // 自动生成基础页面内容
      if (createdPage.id) {
        try {
          const htmlResponse = await qrCodeService.generateHtml(createdPage.id)
          if (htmlResponse.success) {
            // 获取完整的页面详情
            const detailResponse = await qrCodeService.getPage(createdPage.id)
            if (detailResponse.success) {
              // 选择刚创建的页面
              currentPage.value = detailResponse.data
              fillForm(detailResponse.data)
              isDirty.value = false
              
              // 只显示一个成功提示
              toast.success('模板添加成功')
            }
          } else {
            toast.warning('模板添加失败，请重试')
          }
        } catch (htmlError) {
          console.error('生成基础页面失败:', htmlError)
          toast.warning('模板添加失败，请重试')
        }
      }
      
      // 刷新页面列表
      await loadPages()
      
    } else {
      toast.error(response.message || '模板添加失败')
    }
  } catch (error) {
    console.error('创建页面失败:', error)
    toast.error('模板添加失败，请稍后重试')
  } finally {
    addingTemplateId.value = null
  }
}

const resetForm = () => {
  formData.name = ''
  formData.page_title = ''
  formData.page_keywords = ''
  formData.page_description = ''
  formData.amazon_asin = ''
  formData.preset_subject = ''
  formData.preset_content = ''
  formData.customer_service_email = ''
  formData.customer_group = null
  formData.customer_source = ''
  formData.template = ''
  formData.brand_logo = ''
  formData.html_content = ''
  formData.use_custom_html = true
  formData.is_active = true
  formData.product_ids = []
  formData.custom_domain = ''
  selectedProducts.value = []
  boundProducts.value = []
  productSearchQuery.value = ''
  productSearchResults.value = []
  domainStatus.value = {
    verified: false,
    ssl_enabled: false,
    full_url: '',
    instructions: {}
  }
}

const fillForm = (page) => {
  formData.name = page.name || ''
  formData.page_title = page.page_title || ''
  formData.page_keywords = page.page_keywords || ''
  formData.page_description = page.page_description || ''
  formData.amazon_asin = page.amazon_asin || ''
  formData.preset_subject = page.preset_subject || ''
  formData.preset_content = page.preset_content || ''
  formData.customer_service_email = page.customer_service_email || ''
  formData.customer_group = page.customer_group || null
  formData.customer_source = page.customer_source || ''
  formData.template = page.template?.id || page.template || ''
  formData.brand_logo = page.brand_logo || ''
  formData.html_content = page.html_content || ''
  formData.use_custom_html = true
  formData.is_active = page.is_active !== false
  formData.custom_domain = page.custom_domain || ''
  
  // 处理产品绑定
  formData.product_ids = page.products ? page.products.map(p => p.id) : []
  boundProducts.value = page.products ? [...page.products] : []
  selectedProducts.value = page.products ? page.products.map(p => p.id) : []
  
  // 更新域名状态
  domainStatus.value = {
    verified: page.domain_verified || false,
    ssl_enabled: page.ssl_enabled || false,
    full_url: page.full_url || '',
    instructions: {}
  }
  
  // 如果有自定义域名，加载域名状态
  if (page.custom_domain && page.id) {
    refreshDomainStatus()
  }
}

const markDirty = () => {
  isDirty.value = true
}


const savePage = async () => {
  if (!formData.name) {
    toast.error('请填写页面名称')
    return
  }
  
  if (!formData.html_content) {
    toast.error('请填写HTML内容')
    return
  }
  
  // 记录保存前的域名，用于检测域名是否变化
  const oldDomain = currentPage.value?.custom_domain || ''
  const newDomain = formData.custom_domain || ''
  const domainChanged = isEditing.value && oldDomain !== newDomain
  
  isSaving.value = true
  try {
    // 从编辑器获取清理后的HTML（一次性清除所有编辑器标记）
    if (editorInstance.value && editorInstance.value.getCleanHTML) {
      formData.html_content = editorInstance.value.getCleanHTML()
    }
    
    const pageData = {
      ...formData,
      product_ids: formData.product_ids
    }
    
    // 处理brand_logo字段：只有在编辑模式下且logo未改变时才删除此字段
    if (isEditing.value && typeof pageData.brand_logo === 'string' && 
        pageData.brand_logo === currentPage.value?.brand_logo) {
      // 如果是编辑模式，且brand_logo是字符串URL，且与当前页面的logo相同
      // 说明用户没有更改logo，删除此字段避免重复提交
      delete pageData.brand_logo
    }
    // 如果是新上传的logo（URL不同）或者是创建模式，保留brand_logo字段
    
    let response
    if (isEditing.value) {
      response = await qrCodeService.updatePage(currentPage.value.id, pageData)
    } else {
      response = await qrCodeService.createPage(pageData)
    }
    
    if (response.success) {
      
      // 如果域名发生了变化，重新加载完整的页面数据（包含新的二维码）
      if (domainChanged) {
        const detailResponse = await qrCodeService.getPage(currentPage.value.id)
        if (detailResponse.success) {
          currentPage.value = detailResponse.data
          toast.success('页面已更新，二维码已重新生成')
        }
      } else if (!isEditing.value && response.data?.id) {
        // 创建新页面时，重新加载获取完整数据
        const detailResponse = await qrCodeService.getPage(response.data.id)
        if (detailResponse.success) {
          currentPage.value = detailResponse.data
        }
      } else {
        // 编辑模式且域名未变化，直接使用响应数据
        currentPage.value = response.data
      }
      
      isDirty.value = false
      await loadPages()
    } else {
      toast.error(response.message || '保存失败')
    }
  } catch (error) {
    toast.error('保存失败，请稍后重试')
    console.error('保存页面失败:', error)
  } finally {
    isSaving.value = false
  }
}

// 全屏编辑器相关方法
const openFullscreenEditor = () => {
  if (!currentPage.value) {
    toast.error('请先选择一个页面')
    return
  }
  
  fullscreenHtmlContent.value = formData.html_content || ''
  showFullscreenEditor.value = true
}

const saveFullscreenAndClose = () => {
  // 从全屏编辑器获取清理后的HTML
  if (fullscreenHtmlEditorInstance.value && fullscreenHtmlEditorInstance.value.getCleanHTML) {
    formData.html_content = fullscreenHtmlEditorInstance.value.getCleanHTML()
  } else {
    formData.html_content = fullscreenHtmlContent.value
  }
  markDirty()
  toast.success('HTML内容已更新')
  closeFullscreenEditor()
}

const closeFullscreenEditor = () => {
  showFullscreenEditor.value = false
}

const duplicatePage = async (page) => {
  // 防止重复点击
  if (duplicatingPageId.value === page.id) return
  
  duplicatingPageId.value = page.id
  
  try {
    const response = await qrCodeService.duplicatePage(page.id)
    if (response.success) {
      toast.success('页面复制成功')
      await loadPages()
      selectPage(response.data)
    } else {
      toast.error(response.message || '复制失败')
    }
  } catch (error) {
    toast.error('复制失败，请稍后重试')
    console.error('复制页面失败:', error)
  } finally {
    duplicatingPageId.value = null
  }
}

const downloadQRCode = async (page) => {
  if (!page.qr_code_url) {
    toast.error('该页面暂无二维码，请先保存页面')
    return
  }
  
  try {
    // 创建一个隐藏的链接元素来下载文件
    const link = document.createElement('a')
    link.href = page.qr_code_url
    link.download = `qr-code-${page.name}.png`
    link.target = '_blank'
    
    // 对于跨域图片，需要先转换为blob
    const response = await fetch(page.qr_code_url)
    if (response.ok) {
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      link.href = url
      
      // 触发下载
      document.body.appendChild(link)
      link.click()
      
      // 清理
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      
      toast.success('二维码下载成功')
    } else {
      toast.error('下载失败，无法获取二维码图片')
    }
  } catch (error) {
    console.error('下载二维码失败:', error)
    // 如果fetch失败，尝试直接下载
    try {
      const link = document.createElement('a')
      link.href = page.qr_code_url
      link.download = `qr-code-${page.name}.png`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      toast.success('二维码下载成功')
    } catch (fallbackError) {
      toast.error('下载失败，请稍后重试')
      console.error('二维码下载失败:', fallbackError)
    }
  }
}

const deletePage = async (page) => {
  if (!confirm(`确定要删除页面"${page.name}"吗？`)) {
    return
  }

  deletingPageId.value = page.id
  try {
    const response = await qrCodeService.deletePage(page.id)
    if (response.success) {
      toast.success('页面删除成功')
      if (currentPage.value?.id === page.id) {
        currentPage.value = null
        resetForm()
      }
      const nextPage = allPages.value.length <= 1 && listPage.value > 1 ? listPage.value - 1 : listPage.value
      await loadPages(nextPage)
    } else {
      toast.error(response.message || '删除失败')
    }
  } catch (error) {
    toast.error('删除失败，请稍后重试')
    console.error('删除页面失败:', error)
  } finally {
    deletingPageId.value = null
  }
}

const previewPage = () => {
  if (currentPage.value?.uuid) {
    const baseUrl = window.location.origin
    const previewUrl = `${baseUrl}/qr-codes/${currentPage.value.uuid}/`
    window.open(previewUrl, '_blank')
  }
}

const generateHtml = async () => {
  if (!currentPage.value?.id) return
  
  // 确认对话框
  if (!confirm('确定要生成基础页面吗？\n\n此操作将把当前页面重新生成为模板的初始状态，您当前的修改将会丢失。')) {
    return
  }
  
  isGeneratingHtml.value = true
  try {
    const response = await qrCodeService.generateHtml(currentPage.value.id)
    if (response.success) {
      toast.success('基础页面生成成功')
      // 重新加载页面详情
      await selectPage(currentPage.value)
    } else {
      toast.error(response.message || '基础页面生成失败')
    }
  } catch (error) {
    toast.error('基础页面生成失败')
    console.error('基础页面生成失败:', error)
  } finally {
    isGeneratingHtml.value = false
  }
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffTime = Math.abs(now - date)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  if (diffDays === 1) {
    return '昨天'
  } else if (diffDays < 7) {
    return `${diffDays}天前`
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7)
    return `${weeks}周前`
  } else {
    return date.toLocaleDateString('zh-CN')
  }
}

const formatDateTime = (dateString) => {
  return new Date(dateString).toLocaleString('zh-CN')
}

const copyUrl = async (url) => {
  try {
    await navigator.clipboard.writeText(url)
    toast.success('链接已复制到剪贴板')
  } catch (error) {
    console.error('复制失败:', error)
    toast.error('复制失败，请手动复制')
  }
}

// Logo上传相关方法
const triggerFileInput = () => {
  fileInput.value?.click()
}

const handleDragOver = (e) => {
  e.preventDefault()
  isDragOver.value = true
}

const handleDragLeave = (e) => {
  e.preventDefault()
  isDragOver.value = false
}

const handleDrop = (e) => {
  e.preventDefault()
  isDragOver.value = false
  const files = e.dataTransfer.files
  if (files.length > 0) {
    handleFile(files[0])
  }
}

const handleFileSelect = (e) => {
  const file = e.target.files[0]
  if (file) {
    handleFile(file)
  }
}

const validateFile = (file) => {
  // 检查文件类型
  if (!file.type.startsWith('image/')) {
    toast.error('请选择图片文件')
    return false
  }
  
  // 检查文件大小 (5MB)
  if (file.size > 5 * 1024 * 1024) {
    toast.error('文件大小不能超过5MB')
    return false
  }
  
  return true
}

const handleFile = async (file) => {
  if (!validateFile(file)) return
  
  uploadingLogo.value = true
  uploadProgress.value = 0
  
  try {
    const uploadFormData = new FormData()
    uploadFormData.append('upload', file)  // 后端期望的字段名是 'upload'
    
    // 使用axios上传，支持上传进度
    const response = await api.post('/email-templates/api/v2/templates/upload_image/', uploadFormData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.lengthComputable) {
          uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        }
      }
    })
    
    const result = response.data
    
    // 检查上传是否成功
    if (result.success && result.url) {
      // 更新formData中的brand_logo
      formData.brand_logo = result.url
    } else {
      throw new Error(result.message || '上传失败')
    }
    markDirty()
    toast.success('Logo上传成功')
    
  } catch (error) {
    console.error('上传失败:', error)
    let errorMessage = 'Logo上传失败，请稍后重试'
    
    if (error.response?.data?.message) {
      // 后端返回的具体错误信息
      errorMessage = error.response.data.message
    } else if (error.message && error.message !== 'Request failed with status code 400') {
      // 其他错误信息
      errorMessage = error.message
    }
    
    toast.error(errorMessage)
  } finally {
    uploadingLogo.value = false
    uploadProgress.value = 0
    // 清空文件输入
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  }
}

const changeLogo = () => {
  triggerFileInput()
}

const removeLogo = () => {
  if (confirm('确定要删除当前Logo吗？')) {
    formData.brand_logo = ''
    markDirty()
    toast.success('Logo已删除')
  }
}

const handleImageError = (e) => {
  console.error('图片加载失败:', e)
  e.target.style.display = 'none'
  toast.error('Logo图片加载失败')
}

// 自定义域名相关方法
const refreshDomainStatus = async () => {
  if (!currentPage.value?.id) return
  
  isRefreshingDomain.value = true
  try {
    const response = await qrCodeService.getDomainStatus(currentPage.value.id)
    if (response.success) {
      domainStatus.value = {
        verified: response.data.domain_verified || false,
        ssl_enabled: response.data.ssl_enabled || false,
        full_url: response.data.full_url || '',
        instructions: response.data.instructions || {}
      }
    }
  } catch (error) {
    console.error('获取域名状态失败:', error)
  } finally {
    isRefreshingDomain.value = false
  }
}

const verifyDomain = async () => {
  if (!currentPage.value?.id) {
    toast.error('请先保存页面')
    return
  }
  
  if (!formData.custom_domain) {
    toast.error('请先输入自定义域名')
    return
  }
  
  // 先保存自定义域名
  if (isDirty.value) {
    await savePage()
  }
  
  isVerifyingDomain.value = true
  try {
    const response = await qrCodeService.verifyDomain(currentPage.value.id)
    
    if (response.success) {
      // 刷新域名状态
      await refreshDomainStatus()
      // 重新加载页面详情
      await selectPage(currentPage.value)
    } else {
      toast.error(response.message || '域名验证失败')
    }
  } catch (error) {
    console.error('验证域名失败:', error)
    toast.error('验证域名失败，请稍后重试')
  } finally {
    isVerifyingDomain.value = false
  }
}

const oneClickSetup = async () => {
  if (!currentPage.value?.id) return
  
  isSettingUpSSL.value = true
  try {
    const response = await qrCodeService.applySSL(currentPage.value.id)
    
    if (response.success) {
      toast.success('SSL证书申请已提交，预计需要1-2分钟')
      
      // 开始轮询检查SSL状态
      let retryCount = 0
      const maxRetries = 20 // 最多轮询20次（约2分钟）
      
      const checkSSLStatus = setInterval(async () => {
        retryCount++
        
        if (retryCount > maxRetries) {
          clearInterval(checkSSLStatus)
          isSettingUpSSL.value = false
          toast.warning('SSL证书申请耗时较长，请稍后刷新页面查看状态')
          return
        }
        
        const statusResponse = await qrCodeService.getDomainStatus(currentPage.value.id)
        if (statusResponse.success && statusResponse.data.ssl_enabled) {
          clearInterval(checkSSLStatus)
          isSettingUpSSL.value = false
          toast.success('SSL证书已成功启用！')
          await refreshDomainStatus()
          await selectPage(currentPage.value)
        }
      }, 6000) // 每6秒检查一次
    } else {
      toast.error(response.message || 'SSL证书申请失败')
      isSettingUpSSL.value = false
    }
  } catch (error) {
    console.error('申请SSL失败:', error)
    toast.error('申请SSL失败，请稍后重试')
    isSettingUpSSL.value = false
  }
}

// 模态框相关方法
const openDomainModal = async () => {
  if (!currentPage.value) {
    toast.error('请先选择一个页面')
    return
  }
  
  // 根据当前页面的域名状态决定从哪一步开始
  if (formData.custom_domain) {
    // 已有域名，填充到模态框
    modalDomain.value = formData.custom_domain
    
    if (currentPage.value.domain_verified && !currentPage.value.ssl_enabled) {
      // 域名已验证但SSL未配置 → 直接跳到第3步
      modalStep.value = 3
      await refreshDomainStatus()
    } else if (currentPage.value.domain_verified && currentPage.value.ssl_enabled) {
      // 域名已验证且SSL已配置 → 从第1步开始（用户可能想更换域名）
      modalStep.value = 1
    } else {
      // 域名未验证 → 从第2步开始
      modalStep.value = 2
    }
  } else {
    // 没有域名 → 从第1步开始
    modalDomain.value = ''
    modalStep.value = 1
  }
  
  showDomainModal.value = true
}

const closeDomainModal = () => {
  showDomainModal.value = false
  // 清空模态框中的域名输入
  modalDomain.value = ''
  // 重置步骤
  modalStep.value = 1
}

const clearDomain = async () => {
  if (!currentPage.value) return
  
  // 确认对话框
  if (!confirm(`确定要清空自定义域名吗？\n\n当前域名：${formData.custom_domain}\n\n清空后，相关的DNS验证和SSL证书配置将被重置。`)) {
    return
  }
  
  try {
    // 清空域名
    formData.custom_domain = ''
    
    // 保存更改
    await savePage()
    
    // 重置域名状态
    domainStatus.value = {
      verified: false,
      ssl_enabled: false,
      full_url: '',
      instructions: {}
    }
    
    
    // 重新加载页面数据
    await selectPage(currentPage.value)
  } catch (error) {
    console.error('清空域名失败:', error)
    toast.error('清空域名失败，请稍后重试')
  }
}

// 打开访问统计模态框
const openVisitStatsModal = () => {
  if (!currentPage.value) {
    toast.error('请先选择一个页面')
    return
  }
  showVisitStatsModal.value = true
}

// 打开表单提交统计模态框
const openSubmissionStatsModal = () => {
  if (!currentPage.value) {
    toast.error('请先选择一个页面')
    return
  }
  showSubmissionStatsModal.value = true
}

const saveAndNextStep = async () => {
  if (!modalDomain.value) {
    toast.error('请输入域名')
    return
  }
  
  if (!currentPage.value?.id) {
    toast.error('请先选择一个页面')
    return
  }
  
  // 第一步：检查域名是否已被占用
  isSaving.value = true
  try {
    const response = await qrCodeService.checkDomain(currentPage.value.id, modalDomain.value)
    
    if (response.success && response.available) {
      // 域名可用，进入第二步
      modalStep.value = 2
    } else {
      // 域名已被占用
      toast.error(response.message || '该域名已被占用')
    }
  } catch (error) {
    console.error('检查域名失败:', error)
    toast.error('检查域名失败，请稍后重试')
  } finally {
    isSaving.value = false
  }
}

const verifyDomainInModal = async () => {
  if (!currentPage.value?.id) {
    toast.error('请先保存页面')
    return
  }
  
  if (!modalDomain.value) {
    toast.error('请输入域名')
    return
  }
  
  isVerifyingDomain.value = true
  try {
    // 第一步：临时保存域名到数据库（用于DNS验证）
    const pageData = { 
      ...formData,
      custom_domain: modalDomain.value
    }
    const saveResponse = await qrCodeService.updatePage(currentPage.value.id, pageData)
    
    if (!saveResponse.success) {
      toast.error(saveResponse.message || '保存域名失败')
      return
    }
    
    // 第二步：验证DNS
    const response = await qrCodeService.verifyDomain(currentPage.value.id)
    
    // 严格检查：success 必须为 true 且 data.verified 也为 true
    if (response.success === true && response.data?.verified === true) {
      // DNS验证成功，手动更新 currentPage 的验证状态（不触发 fillForm）
      if (currentPage.value) {
        currentPage.value.domain_verified = true
        currentPage.value.domain_verified_at = response.data.verified_at
        currentPage.value.custom_domain = modalDomain.value
      }
      
      // 刷新域名状态
      await refreshDomainStatus()
      
      // ⚠️ 注意：不更新前端的 formData.custom_domain，等待SSL申请成功后再更新
      toast.success('DNS验证成功，请继续申请SSL')
      modalStep.value = 3 // 进入第三步（申请SSL）
    } else {
      // 验证失败，清空数据库中的域名
      const clearData = { ...formData, custom_domain: '' }
      await qrCodeService.updatePage(currentPage.value.id, clearData)
      await selectPage(currentPage.value)
      
      // 显示具体的错误信息
      const errorMsg = response.message || response.data?.message || response.error || '域名验证失败，请检查DNS配置'
      toast.error(errorMsg)
      console.error('域名验证失败:', response)
    }
  } catch (error) {
    console.error('验证域名失败:', error)
    toast.error('验证域名失败，请稍后重试')
  } finally {
    isVerifyingDomain.value = false
  }
}

const applySSLInModal = async () => {
  if (!currentPage.value?.id) return
  
  isSettingUpSSL.value = true
  try {
    const response = await qrCodeService.applySSL(currentPage.value.id)
    
    if (response.success) {
      toast.success('SSL证书申请已提交，预计需要1-2分钟')
      
      // 开始轮询检查SSL状态
      let retryCount = 0
      const maxRetries = 20
      
      const checkSSLStatus = setInterval(async () => {
        retryCount++
        
        if (retryCount > maxRetries) {
          clearInterval(checkSSLStatus)
          isSettingUpSSL.value = false
          
          // SSL申请超时，清空域名配置
          toast.error('SSL证书申请超时，请稍后重试')
          const clearData = { ...formData, custom_domain: '' }
          await qrCodeService.updatePage(currentPage.value.id, clearData)
          await selectPage(currentPage.value)
          
          // 关闭模态框
          closeDomainModal()
          return
        }
        
        const statusResponse = await qrCodeService.getDomainStatus(currentPage.value.id)
        if (statusResponse.success && statusResponse.data.ssl_enabled) {
          clearInterval(checkSSLStatus)
          isSettingUpSSL.value = false
          
          // SSL成功启用，现在才更新前端的 formData.custom_domain
          formData.custom_domain = modalDomain.value
          isDirty.value = false
          
          toast.success('SSL证书已成功启用，域名配置完成！')
          await refreshDomainStatus()
          await selectPage(currentPage.value)
          
          // 关闭模态框
          closeDomainModal()
        }
      }, 6000)
    } else {
      // SSL申请失败，清空域名配置
      toast.error(response.message || 'SSL证书申请失败')
      const clearData = { ...formData, custom_domain: '' }
      await qrCodeService.updatePage(currentPage.value.id, clearData)
      await selectPage(currentPage.value)
      
      isSettingUpSSL.value = false
    }
  } catch (error) {
    console.error('申请SSL失败:', error)
    toast.error('申请SSL失败，请稍后重试')
    
    // SSL申请异常，清空域名配置
    const clearData = { ...formData, custom_domain: '' }
    await qrCodeService.updatePage(currentPage.value.id, clearData)
    await selectPage(currentPage.value)
    
    isSettingUpSSL.value = false
  }
}

// 监听器
watch(() => [formData.name, formData.html_content], () => {
  if (currentPage.value) {
    nextTick(() => {
      if (currentPage.value) {
        markDirty()
      }
    })
  }
}, { deep: true })

// 生命周期
onMounted(async () => {
  await Promise.all([
    loadPages(),
    loadTemplates(),
    loadProducts(),
    loadCustomerGroups()
  ])
})

// 组件销毁时清理资源
onUnmounted(() => {
  // 清除产品搜索定时器
  if (searchTimeout) {
    clearTimeout(searchTimeout)
    searchTimeout = null
  }
})
</script>

<style scoped>
/* 自定义滚动条 */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* 文本截断样式 */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 自定义宽度 */
.w-info {
  width: 24rem; /* 384px - 信息面板宽度 */
}

/* 自适应高度计算 */
.qr-editor-container {
  height: calc(100vh - 116px - 2rem); /* 减去导航栏 + tab栏 + IframeLayout padding */
}

/* 响应式调整 */
@media (min-width: 768px) {
  .qr-editor-container {
    height: calc(100vh - 102px - 3rem); /* md:p-6 */
  }
}

/* 4栏布局优化 */
@media (max-width: 1600px) {
  .w-80 {
    width: 18rem;
  }
  .w-info {
    width: 20rem;
  }
}

@media (max-width: 1400px) {
  /* 隐藏模板栏 */
  .qr-editor-container > .w-80:last-child {
    display: none;
  }
  .w-80 {
    width: 20rem;
  }
  .w-info {
    width: 22rem;
  }
}

@media (max-width: 1024px) {
  .w-80 {
    width: 16rem;
  }
  .w-info {
    width: 20rem;
  }
}

/* 自定义滚动条样式 */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #CBD5E0 #F7FAFC;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #F7FAFC;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #CBD5E0;
  border-radius: 3px;
  transition: background-color 0.2s ease;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #A0AEC0;
}

.custom-scrollbar::-webkit-scrollbar-corner {
  background: #F7FAFC;
}

@media (max-width: 768px) {
  .qr-editor-container {
    flex-direction: column;
    height: calc(100vh - 60px - 2rem);
  }
  
  .w-80, .w-info {
    width: 100%;
    height: auto;
    flex: none;
  }
  
  .flex-1.flex.flex-col {
    height: 60vh;
    flex: 1;
  }
}
</style>